import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
	AuthnService,
	BreadcrumbsService,
	ComponentBase,
	DeploymentContext,
	UserService,
	WebAnalyticsService,
} from '@Common';
import {
	Asset,
	AssetKey,
	Company,
	convertToValidWebsiteUrl,
	IsArrayProperty,
	isNewCompany,
	LocalizedTextIds,
	Mode,
	urlIdentifierForCompany,
} from 'company-finder-common';

@Component({
	selector: 'asset-details',
	templateUrl: './asset-details.component.html',
	styleUrls: ['../asset-details/asset-details.component.scss'],
	standalone: false,
})
export class AssetDetailsComponent extends ComponentBase implements OnInit {
	// Loaded via the resolver
	public asset: Asset;
	public company: Company;

	public isFollowing = false;

	private readonly fullOrderedKeys: AssetKey[] = [
		'sector',
		'focusArea',
		'indication',
		'target',
		'keywords',
		'routeOfAdministration',
		'developmentStage',
		'clinicalOperatingCountry',
		'clinicalTrialNctNumber',
		'regulatoryApprovalStatus',
		'medicalDeviceCategory',
		'medicalDeviceFdaCategory',
	];

	public get AssetKeys() {
		return this.fullOrderedKeys.filter((key) => {
			return !!this.asset[key];
		});
	}

	public constructor(
		private route: ActivatedRoute,
		private authnService: AuthnService,
		private breadcrumbsService: BreadcrumbsService,
		dc: DeploymentContext,
		private router: Router,
		private userService: UserService,
		private webAnalyticsService: WebAnalyticsService
	) {
		super(dc);
	}

	ngOnInit(): void {
		this.asset = this.route.snapshot.data.assetWithCompany.asset;
		this.company = this.route.snapshot.data.assetWithCompany.company;

		this.breadcrumbsService.setDetailsBreadcrumb(
			'asset-detail',
			this.asset.name,
			Mode.Asset
		);

		if (this.isInternalView) {
			this.userService
				.isFollowingCompany(this.company.id)
				.then((isFollowing) => {
					this.isFollowing = isFollowing;
				});
		}
	}

	public getHeaderText(prop: AssetKey): string {
		return this._deploymentContext.getHeaderForAssetProperty(prop);
	}

	public navigateToCompany(): void {
		this.router.navigate([
			'company',
			urlIdentifierForCompany(this.company.name),
		]);
		this.webAnalyticsService.trackEvent(
			'select-company-from-asset-details',
			{
				category: 'Company Details',
				label: `${this.company.name} from ${this.asset.name}`,
			}
		);
	}

	public get encodedName(): string {
		return urlIdentifierForCompany(this.asset.name);
	}

	public get validCompanyWebsiteUrl(): string {
		return convertToValidWebsiteUrl(this.company.website);
	}
	public get isNew(): boolean {
		return isNewCompany(this.company);
	}

	public get isFollowEnabled(): boolean {
		return this.featureSwitches.enableFollowCompanies;
	}

	public get isInternalView(): boolean {
		return this.authnService.isInternal;
	}

	public get suppressStealthMode(): boolean {
		return (
			!this.isInternalView || !this.company || !this.company.isStealthMode
		);
	}

	public async setFollowing(isFollowing: boolean): Promise<void> {
		this.isFollowing = isFollowing;
		this.userService.setFollowingAsset(this.asset.assetId, isFollowing);
	}

	public getValue(prop: AssetKey) {
		const val = this.asset[prop];
		return val && IsArrayProperty(prop, Asset)
			? (val as []).join(', ')
			: val;
	}

	public get assetDetailsTitle(): string {
		return this.Loc(LocalizedTextIds.AssetDetails);
	}

	public get deliverablePartnersTitle(): string {
		return this.Loc(LocalizedTextIds.DeliverablePartner);
	}

	public get assetLabel(): string {
		return this.Loc(LocalizedTextIds.Asset);
	}

	public get companyLabel(): string {
		return this.Loc(LocalizedTextIds.Company);
	}

	public get followLabel(): string {
		return this.isFollowing
			? this.Loc(LocalizedTextIds.Unfollow)
			: this.Loc(LocalizedTextIds.Follow);
	}

	public get newLabel(): string {
		return this.Loc(LocalizedTextIds.New);
	}

	public get stealthLabel(): string {
		return this.Loc(LocalizedTextIds.CompanyDetailsStealth);
	}

	public get developmentStageLabel(): string {
		return this.Loc(LocalizedTextIds.DevelopmentStage);
	}

	public get therapeuticAreaLabel(): string {
		return this.Loc(LocalizedTextIds.TherapeuticArea);
	}
}
